import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, KpiCard, Ring, StatusPill, SegmentedControl, OBLIGATION_STATUS } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { fmtDate } from '../lib/fmt';

type Row = {
  id: string; due_date: string | null; status: string; is_life_safety: boolean; property_id: string;
  properties: { name: string } | null; assets: { name: string } | null;
  asset_obligation_rules: { name_i18n: Record<string, string>; service_line_id: string | null } | null;
};
type SL = { id: string; code: string; name_i18n: Record<string, string> };

const STATUS_FILTERS = ['all', 'overdue', 'due_soon', 'scheduled'] as const;

export function DueDates() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<Row[]>([]);
  const [slById, setSlById] = useState<Record<string, SL>>({});
  const [propertyId, setPropertyId] = useState('');
  const [serviceLine, setServiceLine] = useState('');
  const [statusIdx, setStatusIdx] = useState(0);
  const [kpiOpen, setKpiOpen] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [r, sl] = await Promise.all([
        supabase
          .from('asset_obligations')
          .select('id,due_date,status,is_life_safety,property_id,properties(name),assets(name),asset_obligation_rules(name_i18n,service_line_id)')
          .is('deleted_at', null)
          .order('due_date', { nullsFirst: false }),
        supabase.from('service_lines').select('id,code,name_i18n').is('deleted_at', null),
      ]);
      setRows((r.data as unknown as Row[]) ?? []);
      const m: Record<string, SL> = {};
      ((sl.data as SL[]) ?? []).forEach((x) => { m[x.id] = x; });
      setSlById(m);
      setLoading(false);
    })();
  }, []);

  const properties = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach((r) => r.properties && m.set(r.property_id, r.properties.name));
    return [...m.entries()];
  }, [rows]);
  const slOf = (r: Row) => {
    const id = r.asset_obligation_rules?.service_line_id;
    return id ? slById[id] : undefined;
  };
  const serviceLines = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach((r) => {
      const sl = slOf(r);
      if (sl) m.set(sl.code, sl.name_i18n?.[i18n.language] ?? sl.name_i18n?.en ?? sl.code);
    });
    return [...m.entries()];
  }, [rows, slById, i18n.language]);

  const filtered = rows.filter((r) => {
    if (propertyId && r.property_id !== propertyId) return false;
    if (serviceLine && slOf(r)?.code !== serviceLine) return false;
    const st = STATUS_FILTERS[statusIdx];
    if (st !== 'all' && r.status !== st) return false;
    return true;
  });

  const kpis = useMemo(() => {
    const total = rows.length || 1;
    const overdue = rows.filter((r) => r.status === 'overdue').length;
    const dueSoon = rows.filter((r) => r.status === 'due_soon').length;
    const compliance = Math.round(((total - overdue) / total) * 100);
    const ls = rows.filter((r) => r.is_life_safety);
    const lsCompliance = ls.length ? Math.round(((ls.length - ls.filter((r) => r.status === 'overdue').length) / ls.length) * 100) : 100;
    return { overdue, dueSoon, compliance, lsCompliance };
  }, [rows]);

  const oblName = (r: Row) =>
    r.asset_obligation_rules?.name_i18n?.[i18n.language] ?? r.asset_obligation_rules?.name_i18n?.en ?? '—';
  const slName = (r: Row) => {
    const sl = slOf(r);
    return sl ? (sl.name_i18n?.[i18n.language] ?? sl.name_i18n?.en ?? sl.code) : '—';
  };
  const sel = 'h-9 rounded-md border border-border bg-card px-2.5 text-sm text-fg outline-none';

  return (
    <Layout header={<Topbar />}>
      <div className="flex flex-col gap-[18px] px-7 pt-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.dueDates')}</h1>
          <button className="text-sm text-muted hover:text-fg" onClick={() => setKpiOpen((v) => !v)}>
            {kpiOpen ? t('common.hideKpis') : t('common.showKpis')}
          </button>
        </div>

        {kpiOpen && (
          <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard label={t('kpi.preventiveCompliance')} value={`${kpis.compliance}%`} sub={`${t('kpi.target')} 95%`} dark>
              <Ring value={kpis.compliance} onDark label="" />
            </KpiCard>
            <KpiCard label={t('dd.lifeSafety')} value={`${kpis.lsCompliance}%`} sub={`${t('kpi.target')} 100%`}>
              <Ring value={kpis.lsCompliance} label="" />
            </KpiCard>
            <KpiCard label={t('kpi.overdue')} value={<span className="text-st-overdue-fg">{kpis.overdue}</span>} />
            <KpiCard label={t('status.dueSoon')} value={<span className="text-st-onhold-fg">{kpis.dueSoon}</span>} />
          </section>
        )}
      </div>

      <div className="px-7 pb-7 pt-[18px]">
        <Card className="overflow-hidden p-0">
          <div className="flex flex-wrap items-center gap-2 border-b border-divider p-4">
            <select className={sel} value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
              <option value="">{t('filters.property')}</option>
              {properties.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
            <select className={sel} value={serviceLine} onChange={(e) => setServiceLine(e.target.value)}>
              <option value="">{t('filters.serviceLine')}</option>
              {serviceLines.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
            <div className="ml-auto">
              <SegmentedControl
                items={[t('common.all'), t('status.overdue'), t('status.dueSoon'), t('status.scheduled')]}
                active={statusIdx}
                onChange={setStatusIdx}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="px-5 py-3 font-medium">{t('filters.property')}</th>
                  <th className="px-2 py-3 font-medium">{t('wo.asset')}</th>
                  <th className="px-2 py-3 font-medium">{t('asset.obligation')}</th>
                  <th className="px-2 py-3 font-medium">{t('filters.serviceLine')}</th>
                  <th className="px-2 py-3 font-medium">{t('asset.nextDue')}</th>
                  <th className="px-5 py-3 font-medium">{t('asset.statusCol')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-5 py-6 text-muted">{t('common.loading')}</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>
                ) : (
                  filtered.map((r) => {
                    const sd = OBLIGATION_STATUS[r.status] ?? OBLIGATION_STATUS.scheduled!;
                    return (
                      <tr key={r.id} className="border-t border-divider hover:bg-bg">
                        <td className="px-5 py-3.5 font-medium">
                          {r.properties?.name}
                          {r.is_life_safety && <span className="ml-2 rounded px-1.5 py-0.5 text-2xs font-medium" style={{ background: 'var(--pv-st-inprogress-bg)', color: 'var(--pv-accent)' }}>{t('dd.lifeSafety')}</span>}
                        </td>
                        <td className="px-2 py-3.5 text-muted">{r.assets?.name}</td>
                        <td className="px-2 py-3.5">{oblName(r)}</td>
                        <td className="px-2 py-3.5 text-muted">{slName(r)}</td>
                        <td className="px-2 py-3.5 font-mono">{r.due_date ? fmtDate(r.due_date, i18n.language) : '—'}</td>
                        <td className="px-5 py-3.5"><StatusPill tone={sd.tone}>{t(sd.labelKey)}</StatusPill></td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
